import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import {
  Users, ShoppingBag, BarChart3, ArrowLeft, Search, ChevronLeft, ChevronRight,
  Trash2, Shield, ShieldOff, Crown, CrownIcon, DollarSign, UserCheck, Package,
  CalendarDays, Mail, UserPlus, X, Copy, Check, Contact, Download, Phone, Building,
  Settings, CreditCard, AlertTriangle
} from 'lucide-react';
import { PlexataIcon } from '../components/PlexataLogo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ADMIN_TABS = [
  { id: 'overview', label: 'Overview', icon: BarChart3 },
  { id: 'users', label: 'Users', icon: Users },
  { id: 'contacts', label: 'Contacts', icon: Contact },
  { id: 'orders', label: 'Orders', icon: ShoppingBag },
  { id: 'settings', label: 'Settings', icon: Settings },
];

const StatCard = ({ icon: Icon, label, value, sub, color = '#52ff00' }) => (
  <div data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`} className="bg-[#111] border border-[#222] rounded-xl p-5">
    <div className="flex items-center gap-3 mb-3">
      <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${color}15` }}>
        <Icon size={18} style={{ color }} />
      </div>
      <span className="text-xs text-[#888] uppercase tracking-wider font-medium">{label}</span>
    </div>
    <p className="text-2xl font-bold text-white">{value}</p>
    {sub && <p className="text-xs text-[#666] mt-1">{sub}</p>}
  </div>
);

const AdminDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [usersMeta, setUsersMeta] = useState({ total: 0, page: 1, pages: 1 });
  const [usersSearch, setUsersSearch] = useState('');
  const [usersPage, setUsersPage] = useState(1);
  const [orders, setOrders] = useState([]);
  const [ordersMeta, setOrdersMeta] = useState({ total: 0, page: 1, pages: 1 });
  const [ordersPage, setOrdersPage] = useState(1);
  const [ordersFilter, setOrdersFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  
  // Contacts State
  const [contacts, setContacts] = useState([]);
  const [contactsMeta, setContactsMeta] = useState({ total: 0, page: 1, pages: 1 });
  const [contactsPage, setContactsPage] = useState(1);
  const [contactsSearch, setContactsSearch] = useState('');
  
  // Add User Modal State
  const [showAddUser, setShowAddUser] = useState(false);
  const [addUserForm, setAddUserForm] = useState({ email: '', name: '', plan: 'free', sendEmail: true });
  const [addUserLoading, setAddUserLoading] = useState(false);
  const [addUserResult, setAddUserResult] = useState(null);
  const [copied, setCopied] = useState(false);

  // Stripe Mode State
  const [stripeMode, setStripeMode] = useState('live');
  const [hasKey, setHasKey] = useState(false);
  const [stripeModeLoading, setStripeModeLoading] = useState(false);
  const [stripeModeError, setStripeModeError] = useState('');

  const loadStripeMode = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/stripe-mode`);
      setStripeMode(res.data.mode);
      setHasKey(res.data.has_key);
      setStripeModeError('');
    } catch {}
  }, []);

  const toggleStripeMode = async () => {
    const newMode = stripeMode === 'live' ? 'off' : 'live';
    setStripeModeLoading(true);
    setStripeModeError('');
    try {
      const res = await axios.put(`${API}/admin/stripe-mode`, { mode: newMode });
      setStripeMode(res.data.mode);
    } catch (err) {
      setStripeModeError(err.response?.data?.detail || 'Failed to switch Stripe mode');
    }
    setStripeModeLoading(false);
  };

  const loadStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/stats`);
      setStats(res.data);
    } catch (err) {
      if (err.response?.status === 403) navigate('/dashboard');
    }
  }, [navigate]);

  const loadUsers = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/users`, {
        params: { page: usersPage, limit: 15, search: usersSearch },
      });
      setUsers(res.data.users);
      setUsersMeta({ total: res.data.total, page: res.data.page, pages: res.data.pages });
    } catch {}
  }, [usersPage, usersSearch]);

  const loadOrders = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/orders`, {
        params: { page: ordersPage, limit: 15, status: ordersFilter },
      });
      setOrders(res.data.orders);
      setOrdersMeta({ total: res.data.total, page: res.data.page, pages: res.data.pages });
    } catch {}
  }, [ordersPage, ordersFilter]);

  const loadContacts = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/contacts`, {
        params: { page: contactsPage, limit: 15, search: contactsSearch },
      });
      setContacts(res.data.contacts);
      setContactsMeta({ total: res.data.total, page: res.data.page, pages: res.data.pages });
    } catch {}
  }, [contactsPage, contactsSearch]);

  const exportContacts = async () => {
    try {
      const response = await axios.get(`${API}/admin/contacts/export`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'all_contacts.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      alert('Failed to export contacts');
    }
  };

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await loadStats();
      setLoading(false);
    };
    init();
  }, [loadStats]);

  useEffect(() => {
    if (tab === 'users') loadUsers();
  }, [tab, loadUsers]);

  useEffect(() => {
    if (tab === 'orders') loadOrders();
  }, [tab, loadOrders]);

  useEffect(() => {
    if (tab === 'contacts') loadContacts();
  }, [tab, loadContacts]);

  useEffect(() => {
    if (tab === 'settings') loadStripeMode();
  }, [tab, loadStripeMode]);

  const handleRoleChange = async (userId, newRole) => {
    setActionLoading(userId);
    try {
      await axios.put(`${API}/admin/users/${userId}/role?role=${newRole}`);
      loadUsers();
      loadStats();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to update role');
    }
    setActionLoading(null);
  };

  const handlePlanChange = async (userId, newPlan) => {
    setActionLoading(userId);
    try {
      await axios.put(`${API}/admin/users/${userId}/plan?plan=${newPlan}`);
      loadUsers();
      loadStats();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to update plan');
    }
    setActionLoading(null);
  };

  const handleDeleteUser = async (userId, email) => {
    if (!window.confirm(`Delete user ${email}? This cannot be undone.`)) return;
    setActionLoading(userId);
    try {
      await axios.delete(`${API}/admin/users/${userId}`);
      loadUsers();
      loadStats();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to delete user');
    }
    setActionLoading(null);
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    if (!addUserForm.email.trim()) return;
    
    setAddUserLoading(true);
    setAddUserResult(null);
    
    try {
      const res = await axios.post(`${API}/admin/users/create`, {
        email: addUserForm.email.trim(),
        name: addUserForm.name.trim(),
        plan: addUserForm.plan,
        send_email: addUserForm.sendEmail,
      });
      setAddUserResult(res.data);
      loadUsers();
      loadStats();
    } catch (err) {
      setAddUserResult({ success: false, message: err.response?.data?.detail || 'Failed to create user' });
    }
    setAddUserLoading(false);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const resetAddUserModal = () => {
    setShowAddUser(false);
    setAddUserForm({ email: '', name: '', plan: 'free', sendEmail: true });
    setAddUserResult(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#52ff00] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#050505] text-white">
      {/* Header */}
      <header className="border-b border-[#1a1a1a] bg-[#0a0a0a]/80 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button data-testid="back-to-dashboard-btn" onClick={() => navigate('/dashboard')} className="text-[#888] hover:text-white transition-colors">
              <ArrowLeft size={18} />
            </button>
            <PlexataIcon className="w-6 h-6" />
            <span className="text-sm font-semibold text-white">Admin Panel</span>
            <span className="text-[10px] bg-[#52ff00]/10 text-[#52ff00] px-2 py-0.5 rounded-full font-medium">ADMIN</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-[#666]">{user?.email}</span>
            <button data-testid="admin-logout-btn" onClick={() => { logout(); navigate('/login'); }} className="text-xs text-[#888] hover:text-white transition-colors">
              Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        {/* Tabs */}
        <div data-testid="admin-tabs" className="flex gap-1 bg-[#111] rounded-lg p-1 mb-6 w-fit">
          {ADMIN_TABS.map(t => (
            <button
              key={t.id}
              data-testid={`admin-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${
                tab === t.id ? 'bg-[#1a1a1a] text-[#52ff00]' : 'text-[#888] hover:text-white'
              }`}
            >
              <t.icon size={16} />
              {t.label}
            </button>
          ))}
        </div>

        {/* Overview Tab */}
        {tab === 'overview' && stats && (
          <div data-testid="admin-overview">
            <h2 className="text-lg font-semibold mb-4">Platform Overview</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              <StatCard icon={Users} label="Total Users" value={stats.total_users} sub={`${stats.pro_users} Pro / ${stats.free_users} Free`} />
              <StatCard icon={Package} label="Total Orders" value={stats.total_orders} sub={`${stats.paid_orders} paid`} color="#3b82f6" />
              <StatCard icon={DollarSign} label="Revenue" value={`$${stats.total_revenue.toFixed(2)}`} color="#f59e0b" />
              <StatCard icon={Mail} label="Contacts" value={stats.total_contacts} sub={`${stats.total_bookings} bookings`} color="#a855f7" />
            </div>
          </div>
        )}

        {/* Users Tab */}
        {tab === 'users' && (
          <div data-testid="admin-users">
            <div className="flex flex-col sm:flex-row gap-3 mb-4 items-start sm:items-center justify-between">
              <h2 className="text-lg font-semibold">Users ({usersMeta.total})</h2>
              <div className="flex items-center gap-3">
                <button
                  data-testid="add-user-btn"
                  onClick={() => setShowAddUser(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-[#52ff00] text-black rounded-lg text-sm font-semibold hover:bg-[#4ae600] transition-colors"
                >
                  <UserPlus size={16} /> Add User
                </button>
                <div className="relative">
                  <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#666]" />
                  <input
                    data-testid="admin-users-search"
                    type="text"
                    placeholder="Search users..."
                    value={usersSearch}
                    onChange={e => { setUsersSearch(e.target.value); setUsersPage(1); }}
                    className="pl-9 pr-4 py-2 bg-[#111] border border-[#222] rounded-lg text-sm text-white placeholder-[#555] focus:outline-none focus:border-[#52ff00]/40 w-64"
                  />
                </div>
              </div>
            </div>

            <div className="bg-[#111] border border-[#222] rounded-xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-[#222] text-[#888]">
                      <th className="text-left px-4 py-3 font-medium">User</th>
                      <th className="text-left px-4 py-3 font-medium hidden md:table-cell">Username</th>
                      <th className="text-left px-4 py-3 font-medium hidden lg:table-cell">Provider</th>
                      <th className="text-left px-4 py-3 font-medium">Plan</th>
                      <th className="text-left px-4 py-3 font-medium">Role</th>
                      <th className="text-left px-4 py-3 font-medium hidden lg:table-cell">Joined</th>
                      <th className="text-right px-4 py-3 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map(u => (
                      <tr key={u.id} data-testid={`user-row-${u.id}`} className="border-b border-[#1a1a1a] hover:bg-[#0d0d0d] transition-colors">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            {u.profile?.image_url ? (
                              <img src={u.profile.image_url} alt="" className="w-8 h-8 rounded-full object-cover" />
                            ) : (
                              <div className="w-8 h-8 rounded-full bg-[#222] flex items-center justify-center text-xs text-[#888]">
                                {(u.profile?.name || u.email)?.[0]?.toUpperCase()}
                              </div>
                            )}
                            <div>
                              <p className="text-white font-medium text-sm">{u.profile?.name || 'No name'}</p>
                              <p className="text-[#666] text-xs">{u.email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-[#aaa] hidden md:table-cell">@{u.username}</td>
                        <td className="px-4 py-3 hidden lg:table-cell">
                          <span className={`text-xs px-2 py-0.5 rounded-full ${
                            u.auth_provider === 'google' ? 'bg-blue-500/10 text-blue-400' :
                            u.auth_provider === 'both' ? 'bg-purple-500/10 text-purple-400' :
                            'bg-[#222] text-[#888]'
                          }`}>
                            {u.auth_provider || 'email'}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <button
                            data-testid={`toggle-plan-${u.id}`}
                            onClick={() => handlePlanChange(u.id, u.plan === 'pro' ? 'free' : 'pro')}
                            disabled={actionLoading === u.id}
                            className={`text-xs px-2 py-0.5 rounded-full font-medium transition-colors ${
                              u.plan === 'pro'
                                ? 'bg-amber-500/10 text-amber-400 hover:bg-amber-500/20'
                                : 'bg-[#222] text-[#888] hover:bg-[#333]'
                            }`}
                          >
                            {u.plan === 'pro' ? 'PRO' : 'FREE'}
                          </button>
                        </td>
                        <td className="px-4 py-3">
                          <button
                            data-testid={`toggle-role-${u.id}`}
                            onClick={() => handleRoleChange(u.id, u.role === 'admin' ? 'user' : 'admin')}
                            disabled={actionLoading === u.id || u.id === user?.id}
                            className={`flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium transition-colors ${
                              u.role === 'admin'
                                ? 'bg-[#52ff00]/10 text-[#52ff00] hover:bg-[#52ff00]/20'
                                : 'bg-[#222] text-[#888] hover:bg-[#333]'
                            } ${u.id === user?.id ? 'opacity-50 cursor-not-allowed' : ''}`}
                          >
                            {u.role === 'admin' ? <Shield size={12} /> : <ShieldOff size={12} />}
                            {u.role === 'admin' ? 'Admin' : 'User'}
                          </button>
                        </td>
                        <td className="px-4 py-3 text-[#666] text-xs hidden lg:table-cell">
                          {u.created_at ? new Date(u.created_at).toLocaleDateString() : '-'}
                        </td>
                        <td className="px-4 py-3 text-right">
                          {u.id !== user?.id && (
                            <button
                              data-testid={`delete-user-${u.id}`}
                              onClick={() => handleDeleteUser(u.id, u.email)}
                              disabled={actionLoading === u.id}
                              className="text-[#555] hover:text-red-400 transition-colors p-1"
                              title="Delete user"
                            >
                              <Trash2 size={14} />
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                    {users.length === 0 && (
                      <tr><td colSpan="7" className="px-4 py-8 text-center text-[#666]">No users found</td></tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {usersMeta.pages > 1 && (
                <div data-testid="users-pagination" className="flex items-center justify-between px-4 py-3 border-t border-[#222]">
                  <span className="text-xs text-[#666]">
                    Page {usersMeta.page} of {usersMeta.pages}
                  </span>
                  <div className="flex gap-1">
                    <button
                      onClick={() => setUsersPage(p => Math.max(1, p - 1))}
                      disabled={usersPage <= 1}
                      className="p-1.5 rounded-md bg-[#1a1a1a] text-[#888] hover:text-white disabled:opacity-30 transition-colors"
                    >
                      <ChevronLeft size={14} />
                    </button>
                    <button
                      onClick={() => setUsersPage(p => Math.min(usersMeta.pages, p + 1))}
                      disabled={usersPage >= usersMeta.pages}
                      className="p-1.5 rounded-md bg-[#1a1a1a] text-[#888] hover:text-white disabled:opacity-30 transition-colors"
                    >
                      <ChevronRight size={14} />
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Orders Tab */}
        {tab === 'orders' && (
          <div data-testid="admin-orders">
            <div className="flex flex-col sm:flex-row gap-3 mb-4 items-start sm:items-center justify-between">
              <h2 className="text-lg font-semibold">Orders ({ordersMeta.total})</h2>
              <div className="flex gap-2">
                {['', 'pending', 'paid'].map(f => (
                  <button
                    key={f}
                    data-testid={`orders-filter-${f || 'all'}`}
                    onClick={() => { setOrdersFilter(f); setOrdersPage(1); }}
                    className={`text-xs px-3 py-1.5 rounded-lg font-medium transition-colors ${
                      ordersFilter === f
                        ? 'bg-[#52ff00]/10 text-[#52ff00]'
                        : 'bg-[#111] border border-[#222] text-[#888] hover:text-white'
                    }`}
                  >
                    {f === '' ? 'All' : f.charAt(0).toUpperCase() + f.slice(1)}
                  </button>
                ))}
              </div>
            </div>

            <div className="bg-[#111] border border-[#222] rounded-xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-[#222] text-[#888]">
                      <th className="text-left px-4 py-3 font-medium">Order ID</th>
                      <th className="text-left px-4 py-3 font-medium">Ring</th>
                      <th className="text-left px-4 py-3 font-medium hidden md:table-cell">Card URL</th>
                      <th className="text-left px-4 py-3 font-medium">Price</th>
                      <th className="text-left px-4 py-3 font-medium hidden md:table-cell">Promo</th>
                      <th className="text-left px-4 py-3 font-medium">Status</th>
                      <th className="text-left px-4 py-3 font-medium hidden lg:table-cell">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orders.map(o => (
                      <tr key={o.id || o.session_id} data-testid={`order-row-${o.id || o.session_id}`} className="border-b border-[#1a1a1a] hover:bg-[#0d0d0d] transition-colors">
                        <td className="px-4 py-3 text-[#aaa] font-mono text-xs">
                          {(o.session_id || o.id || '').slice(0, 16)}...
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-white">{o.color}</span>
                          <span className="text-[#666] ml-1">Size {o.size}</span>
                        </td>
                        <td className="px-4 py-3 text-xs text-[#666] max-w-[200px] truncate hidden md:table-cell">
                          {o.card_url || '-'}
                        </td>
                        <td className="px-4 py-3 text-white font-medium">
                          ${o.price?.toFixed(2) || '0.00'}
                        </td>
                        <td className="px-4 py-3 hidden md:table-cell">
                          {o.promo_code ? (
                            <span className="text-xs bg-amber-500/10 text-amber-400 px-2 py-0.5 rounded-full">{o.promo_code}</span>
                          ) : (
                            <span className="text-[#555]">-</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                            o.status === 'paid' ? 'bg-green-500/10 text-green-400' :
                            o.status === 'pending' ? 'bg-yellow-500/10 text-yellow-400' :
                            'bg-[#222] text-[#888]'
                          }`}>
                            {o.status || 'unknown'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-[#666] text-xs hidden lg:table-cell">
                          {o.created_at ? new Date(o.created_at).toLocaleDateString() : '-'}
                        </td>
                      </tr>
                    ))}
                    {orders.length === 0 && (
                      <tr><td colSpan="7" className="px-4 py-8 text-center text-[#666]">No orders found</td></tr>
                    )}
                  </tbody>
                </table>
              </div>

              {ordersMeta.pages > 1 && (
                <div data-testid="orders-pagination" className="flex items-center justify-between px-4 py-3 border-t border-[#222]">
                  <span className="text-xs text-[#666]">
                    Page {ordersMeta.page} of {ordersMeta.pages}
                  </span>
                  <div className="flex gap-1">
                    <button
                      onClick={() => setOrdersPage(p => Math.max(1, p - 1))}
                      disabled={ordersPage <= 1}
                      className="p-1.5 rounded-md bg-[#1a1a1a] text-[#888] hover:text-white disabled:opacity-30 transition-colors"
                    >
                      <ChevronLeft size={14} />
                    </button>
                    <button
                      onClick={() => setOrdersPage(p => Math.min(ordersMeta.pages, p + 1))}
                      disabled={ordersPage >= ordersMeta.pages}
                      className="p-1.5 rounded-md bg-[#1a1a1a] text-[#888] hover:text-white disabled:opacity-30 transition-colors"
                    >
                      <ChevronRight size={14} />
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Contacts Tab */}
        {tab === 'contacts' && (
          <div data-testid="admin-contacts">
            <div className="flex flex-col sm:flex-row gap-3 mb-4 items-start sm:items-center justify-between">
              <h2 className="text-lg font-semibold">All Contacts ({contactsMeta.total})</h2>
              <div className="flex items-center gap-3">
                <button
                  onClick={exportContacts}
                  className="flex items-center gap-2 px-4 py-2 bg-[#1a1a1a] border border-[#333] text-white rounded-lg text-sm font-semibold hover:bg-[#222] transition-colors"
                  data-testid="export-contacts-btn"
                >
                  <Download size={16} /> Export CSV
                </button>
                <div className="relative">
                  <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#666]" />
                  <input
                    data-testid="admin-contacts-search"
                    type="text"
                    placeholder="Search contacts..."
                    value={contactsSearch}
                    onChange={e => { setContactsSearch(e.target.value); setContactsPage(1); }}
                    className="pl-9 pr-4 py-2 bg-[#111] border border-[#222] rounded-lg text-sm text-white placeholder-[#555] focus:outline-none focus:border-[#52ff00]/40 w-64"
                  />
                </div>
              </div>
            </div>

            <div className="bg-[#111] border border-[#222] rounded-xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-[#222] text-[#888]">
                      <th className="text-left px-4 py-3 font-medium">Contact</th>
                      <th className="text-left px-4 py-3 font-medium hidden md:table-cell">Phone</th>
                      <th className="text-left px-4 py-3 font-medium hidden lg:table-cell">Company</th>
                      <th className="text-left px-4 py-3 font-medium">Card Owner</th>
                      <th className="text-left px-4 py-3 font-medium hidden lg:table-cell">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {contacts.map((c, idx) => (
                      <tr key={c.id || idx} data-testid={`contact-row-${c.id || idx}`} className="border-b border-[#1a1a1a] hover:bg-[#0d0d0d] transition-colors">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-[#222] flex items-center justify-center text-xs text-[#888]">
                              {(c.visitor_name || c.visitor_email)?.[0]?.toUpperCase() || '?'}
                            </div>
                            <div>
                              <p className="text-white font-medium text-sm">{c.visitor_name || 'Unknown'}</p>
                              <p className="text-[#666] text-xs">{c.visitor_email || 'No email'}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 hidden md:table-cell">
                          {c.visitor_phone ? (
                            <span className="flex items-center gap-1.5 text-[#aaa]">
                              <Phone size={12} className="text-[#666]" />
                              {c.visitor_phone}
                            </span>
                          ) : (
                            <span className="text-[#555]">-</span>
                          )}
                        </td>
                        <td className="px-4 py-3 hidden lg:table-cell">
                          {c.visitor_company ? (
                            <span className="flex items-center gap-1.5 text-[#aaa]">
                              <Building size={12} className="text-[#666]" />
                              {c.visitor_company}
                            </span>
                          ) : (
                            <span className="text-[#555]">-</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div>
                            <p className="text-white text-sm">{c.owner_name || c.owner_username}</p>
                            <p className="text-[#666] text-xs">@{c.owner_username}</p>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-[#666] text-xs hidden lg:table-cell">
                          {c.created_at ? new Date(c.created_at).toLocaleDateString() : '-'}
                        </td>
                      </tr>
                    ))}
                    {contacts.length === 0 && (
                      <tr><td colSpan="5" className="px-4 py-8 text-center text-[#666]">No contacts found</td></tr>
                    )}
                  </tbody>
                </table>
              </div>

              {contactsMeta.pages > 1 && (
                <div data-testid="contacts-pagination" className="flex items-center justify-between px-4 py-3 border-t border-[#222]">
                  <span className="text-xs text-[#666]">
                    Page {contactsMeta.page} of {contactsMeta.pages}
                  </span>
                  <div className="flex gap-1">
                    <button
                      onClick={() => setContactsPage(p => Math.max(1, p - 1))}
                      disabled={contactsPage <= 1}
                      className="p-1.5 rounded-md bg-[#1a1a1a] text-[#888] hover:text-white disabled:opacity-30 transition-colors"
                    >
                      <ChevronLeft size={14} />
                    </button>
                    <button
                      onClick={() => setContactsPage(p => Math.min(contactsMeta.pages, p + 1))}
                      disabled={contactsPage >= contactsMeta.pages}
                      className="p-1.5 rounded-md bg-[#1a1a1a] text-[#888] hover:text-white disabled:opacity-30 transition-colors"
                    >
                      <ChevronRight size={14} />
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Settings Tab */}
        {tab === 'settings' && (
          <div data-testid="admin-settings">
            <h2 className="text-lg font-semibold mb-6">Platform Settings</h2>

            {/* Stripe Payment Mode */}
            <div data-testid="stripe-mode-section" className="bg-[#111] border border-[#222] rounded-xl p-6 max-w-2xl">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-[#1a1a1a] flex items-center justify-center">
                  <CreditCard size={20} className="text-[#52ff00]" />
                </div>
                <div>
                  <h3 className="text-base font-semibold text-white">Stripe Payments</h3>
                  <p className="text-xs text-[#666]">Enable or disable live Stripe payments</p>
                </div>
              </div>

              <div className="flex items-center justify-between p-4 bg-[#0a0a0a] rounded-lg border border-[#1a1a1a]">
                <div className="flex items-center gap-4">
                  <span className={`text-sm font-semibold ${stripeMode === 'off' ? 'text-white' : 'text-[#555]'}`}>
                    Off
                  </span>
                  <button
                    data-testid="stripe-mode-toggle"
                    onClick={toggleStripeMode}
                    disabled={stripeModeLoading}
                    className={`relative w-14 h-7 rounded-full transition-colors duration-200 ${
                      stripeMode === 'live' ? 'bg-[#52ff00]' : 'bg-[#333]'
                    } ${stripeModeLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                  >
                    <span className={`absolute top-0.5 w-6 h-6 rounded-full bg-white shadow-md transition-transform duration-200 ${
                      stripeMode === 'live' ? 'translate-x-7' : 'translate-x-0.5'
                    }`} />
                  </button>
                  <span className={`text-sm font-semibold ${stripeMode === 'live' ? 'text-white' : 'text-[#555]'}`}>
                    Live
                  </span>
                </div>

                <span data-testid="stripe-mode-badge" className={`text-xs px-3 py-1 rounded-full font-bold uppercase tracking-wider ${
                  stripeMode === 'live'
                    ? 'bg-[#52ff00]/10 text-[#52ff00] border border-[#52ff00]/20'
                    : 'bg-red-500/10 text-red-400 border border-red-500/20'
                }`}>
                  {stripeMode === 'live' ? 'Active' : 'Disabled'}
                </span>
              </div>

              {/* Key status */}
              <div className="mt-4">
                <div className={`flex items-center gap-2 p-3 rounded-lg border ${
                  hasKey ? 'bg-[#0a0a0a] border-[#1a1a1a]' : 'bg-red-500/5 border-red-500/20'
                }`}>
                  <div className={`w-2 h-2 rounded-full ${hasKey ? 'bg-[#52ff00]' : 'bg-red-500'}`} />
                  <span className="text-xs text-[#888]">Stripe API Key</span>
                  <span data-testid="stripe-key-status" className={`text-xs font-medium ml-auto ${hasKey ? 'text-[#52ff00]' : 'text-red-400'}`}>
                    {hasKey ? 'Configured' : 'Missing'}
                  </span>
                </div>
              </div>

              {/* Error */}
              {stripeModeError && (
                <div data-testid="stripe-mode-error" className="mt-4 flex items-start gap-2 p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
                  <AlertTriangle size={16} className="text-red-400 shrink-0 mt-0.5" />
                  <p className="text-sm text-red-400">{stripeModeError}</p>
                </div>
              )}

              {/* Info message */}
              {stripeMode === 'live' && (
                <div className="mt-4 flex items-start gap-2 p-3 bg-[#52ff00]/5 border border-[#52ff00]/10 rounded-lg">
                  <CreditCard size={16} className="text-[#52ff00] shrink-0 mt-0.5" />
                  <p className="text-xs text-[#888]">
                    <span className="text-[#52ff00] font-medium">Payments enabled.</span> All transactions will be processed with real charges through Stripe.
                  </p>
                </div>
              )}
              {stripeMode === 'off' && (
                <div className="mt-4 flex items-start gap-2 p-3 bg-red-500/5 border border-red-500/10 rounded-lg">
                  <AlertTriangle size={16} className="text-red-400 shrink-0 mt-0.5" />
                  <p className="text-xs text-[#888]">
                    <span className="text-red-400 font-medium">Payments disabled.</span> Customers will not be able to complete purchases until payments are re-enabled.
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Add User Modal */}
      {showAddUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm" data-testid="add-user-modal">
          <div className="bg-[#111] border border-[#222] rounded-xl w-full max-w-md mx-4 shadow-2xl">
            <div className="flex items-center justify-between p-4 border-b border-[#222]">
              <h3 className="text-lg font-semibold text-white">Add New User</h3>
              <button onClick={resetAddUserModal} className="p-1 text-[#666] hover:text-white transition-colors">
                <X size={20} />
              </button>
            </div>

            {!addUserResult ? (
              <form onSubmit={handleAddUser} className="p-4 space-y-4">
                <div>
                  <label className="block text-xs font-semibold text-[#888] mb-1.5">Email Address *</label>
                  <input
                    type="email"
                    value={addUserForm.email}
                    onChange={e => setAddUserForm(f => ({ ...f, email: e.target.value }))}
                    placeholder="user@example.com"
                    required
                    className="w-full px-3 py-2.5 bg-[#0a0a0a] border border-[#222] rounded-lg text-white placeholder-[#555] focus:outline-none focus:border-[#52ff00]/40"
                    data-testid="add-user-email"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-[#888] mb-1.5">Name (optional)</label>
                  <input
                    type="text"
                    value={addUserForm.name}
                    onChange={e => setAddUserForm(f => ({ ...f, name: e.target.value }))}
                    placeholder="John Doe"
                    className="w-full px-3 py-2.5 bg-[#0a0a0a] border border-[#222] rounded-lg text-white placeholder-[#555] focus:outline-none focus:border-[#52ff00]/40"
                    data-testid="add-user-name"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-[#888] mb-1.5">Plan</label>
                  <select
                    value={addUserForm.plan}
                    onChange={e => setAddUserForm(f => ({ ...f, plan: e.target.value }))}
                    className="w-full px-3 py-2.5 bg-[#0a0a0a] border border-[#222] rounded-lg text-white focus:outline-none focus:border-[#52ff00]/40"
                    data-testid="add-user-plan"
                  >
                    <option value="free">Free</option>
                    <option value="pro">Pro</option>
                  </select>
                </div>
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => setAddUserForm(f => ({ ...f, sendEmail: !f.sendEmail }))}
                    className={`relative w-10 h-5 rounded-full transition-colors ${addUserForm.sendEmail ? 'bg-[#52ff00]' : 'bg-[#333]'}`}
                  >
                    <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${addUserForm.sendEmail ? 'left-5' : 'left-0.5'}`} />
                  </button>
                  <label className="text-sm text-[#888]">Send welcome email with login credentials</label>
                </div>
                <div className="pt-2 flex gap-3">
                  <button
                    type="button"
                    onClick={resetAddUserModal}
                    className="flex-1 py-2.5 bg-[#222] text-white rounded-lg text-sm font-semibold hover:bg-[#333] transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={addUserLoading}
                    className="flex-1 py-2.5 bg-[#52ff00] text-black rounded-lg text-sm font-semibold hover:bg-[#4ae600] transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                    data-testid="add-user-submit"
                  >
                    {addUserLoading ? (
                      <div className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                    ) : (
                      <>
                        <UserPlus size={16} /> Create User
                      </>
                    )}
                  </button>
                </div>
              </form>
            ) : (
              <div className="p-4">
                {addUserResult.success ? (
                  <div className="space-y-4">
                    <div className="flex items-center gap-3 p-3 bg-green-500/10 border border-green-500/20 rounded-lg">
                      <Check className="w-5 h-5 text-green-500 shrink-0" />
                      <p className="text-sm text-green-400">{addUserResult.message}</p>
                    </div>
                    
                    <div className="bg-[#0a0a0a] rounded-lg p-4 space-y-3">
                      <div>
                        <p className="text-xs text-[#666] mb-1">Email</p>
                        <p className="text-sm text-white font-medium">{addUserResult.user?.email}</p>
                      </div>
                      <div>
                        <p className="text-xs text-[#666] mb-1">Username</p>
                        <p className="text-sm text-white font-medium">@{addUserResult.user?.username}</p>
                      </div>
                      {addUserResult.temp_password && (
                        <div>
                          <p className="text-xs text-[#666] mb-1">Temporary Password</p>
                          <div className="flex items-center gap-2">
                            <code className="text-sm text-[#52ff00] font-mono bg-[#1a1a1a] px-2 py-1 rounded">{addUserResult.temp_password}</code>
                            <button
                              onClick={() => copyToClipboard(addUserResult.temp_password)}
                              className="p-1.5 text-[#666] hover:text-white transition-colors"
                            >
                              {copied ? <Check size={14} className="text-green-500" /> : <Copy size={14} />}
                            </button>
                          </div>
                          <p className="text-xs text-[#555] mt-1">Share this password with the user (email was not sent)</p>
                        </div>
                      )}
                    </div>
                    
                    <button
                      onClick={resetAddUserModal}
                      className="w-full py-2.5 bg-[#222] text-white rounded-lg text-sm font-semibold hover:bg-[#333] transition-colors"
                    >
                      Done
                    </button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="flex items-center gap-3 p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
                      <X className="w-5 h-5 text-red-500 shrink-0" />
                      <p className="text-sm text-red-400">{addUserResult.message}</p>
                    </div>
                    <button
                      onClick={() => setAddUserResult(null)}
                      className="w-full py-2.5 bg-[#222] text-white rounded-lg text-sm font-semibold hover:bg-[#333] transition-colors"
                    >
                      Try Again
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
